import { memo, useMemo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CapturedPieceRecord, Color, PieceType } from '@fog-chess/core';
import { Piece } from './Piece';
import { cn } from '@/lib/utils';

export interface GraveyardProps {
  /** All captured pieces from the game */
  graveyard: ReadonlyArray<CapturedPieceRecord>;
  /** Viewer's color (to separate "my captures" vs "opponent captures") */
  viewerColor: Color | null;
  /** Compact mode for mobile */
  compact?: boolean;
  /** Trigger for capture animation (timestamp or counter) */
  captureTrigger?: number;
}

/**
 * Graveyard — Captured pieces panel.
 *
 * Isolated from the 64-square board to prevent re-render cascades.
 * Reads `graveyard` directly from game store and renders two columns:
 * - Pieces I captured (opponent's pieces)
 * - Pieces that captured mine (my pieces taken)
 *
 * Wrapped in React.memo with custom comparator comparing only graveyard length
 * and viewer color changes.
 */
export const Graveyard = memo(function Graveyard({
  graveyard,
  viewerColor,
  compact = false,
  captureTrigger,
}: GraveyardProps) {
  // Track previous graveyard length to detect new captures
  const [prevLength, setPrevLength] = useState(graveyard.length);
  const [lastCaptured, setLastCaptured] = useState<CapturedPieceRecord | null>(null);
  
  // Detect new capture and trigger animation
  useEffect(() => {
    if (graveyard.length > prevLength) {
      const newCapture = graveyard[graveyard.length - 1];
      setLastCaptured(newCapture ?? null);
      
      // Clear animation after delay
      setTimeout(() => {
        setLastCaptured(null);
      }, 800);
    }
    setPrevLength(graveyard.length);
  }, [graveyard, prevLength]);
  
  // Also respond to external trigger (from socket controller)
  useEffect(() => {
    if (captureTrigger && graveyard.length > 0) {
      const newCapture = graveyard[graveyard.length - 1];
      setLastCaptured(newCapture ?? null);
      setTimeout(() => setLastCaptured(null), 800);
    }
  }, [captureTrigger, graveyard]);
  // Separate captures by color
  const { whiteCaptures, blackCaptures } = useMemo(() => {
    const white: CapturedPieceRecord[] = [];
    const black: CapturedPieceRecord[] = [];

    for (const capture of graveyard) {
      if (capture.captured_by_color === 'white') {
        white.push(capture);
      } else {
        black.push(capture);
      }
    }

    return { whiteCaptures: white, blackCaptures: black };
  }, [graveyard]);

  // Determine which column is "my captures" vs "opponent captures"
  const myCaptures = viewerColor === 'white' ? whiteCaptures : blackCaptures;
  const opponentCaptures = viewerColor === 'white' ? blackCaptures : whiteCaptures;

  // Group by piece type for compact display
  const groupCaptures = (captures: CapturedPieceRecord[]) => {
    const groups = new Map<PieceType, number>();
    for (const c of captures) {
      groups.set(c.type, (groups.get(c.type) || 0) + 1);
    }
    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        // Sort by piece value: P < N/B < R < Q
        const order: Record<PieceType, number> = { P: 1, N: 2, B: 3, R: 4, Q: 5, K: 6 };
        return order[a] - order[b];
      })
      .map(([type, count]) => ({ type, count }));
  };

  const myGroups = groupCaptures(myCaptures);
  const opponentGroups = groupCaptures(opponentCaptures);

  const pieceSize = compact ? 24 : 32;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 p-2',
        compact ? 'min-w-[80px]' : 'min-w-[100px]',
      )}
      role="region"
      aria-label="Captured pieces"
    >
      {/* My captures (pieces I took) */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
          <span>Captured</span>
        </div>
        <div
          className={cn(
            'flex flex-wrap items-center justify-start gap-1',
            compact ? 'gap-0.5' : 'gap-1',
          )}
          role="list"
          aria-label="Pieces you captured"
        >
          {myGroups.length === 0 ? (
            <span className="text-xs text-muted-foreground/60 w-full text-center py-1">
              None
            </span>
          ) : (
            myGroups.map(({ type, count }) => {
              const isNewCapture = lastCaptured && 
                lastCaptured.type === type && 
                lastCaptured.captured_by_color === (viewerColor === 'white' ? 'white' : 'black');
              
              return (
                <div
                  key={type}
                  className="flex items-center gap-1"
                  role="listitem"
                >
                  <AnimatePresence mode="wait">
                    {isNewCapture ? (
                      <motion.div
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 1.15, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 18, mass: 0.7 }}
                      >
                        <Piece type={type} color={viewerColor === 'white' ? 'black' : 'white'} size={pieceSize} />
                      </motion.div>
                    ) : (
                      <Piece type={type} color={viewerColor === 'white' ? 'black' : 'white'} size={pieceSize} />
                    )}
                  </AnimatePresence>
                  {count > 1 && (
                    <span
                      className={cn(
                        'text-xs font-mono font-semibold text-primary',
                        compact && 'text-[10px]',
                      )}
                    >
                      ×{count}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Opponent captures (my pieces taken) */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <span className="w-1.5 h-1.5 rounded-full bg-destructive" aria-hidden="true" />
          <span>Lost</span>
        </div>
        <div
          className={cn(
            'flex flex-wrap items-center justify-start gap-1',
            compact ? 'gap-0.5' : 'gap-1',
          )}
          role="list"
          aria-label="Your pieces captured"
        >
          {opponentGroups.length === 0 ? (
            <span className="text-xs text-muted-foreground/60 w-full text-center py-1">
              None
            </span>
          ) : (
            opponentGroups.map(({ type, count }) => {
              const isNewCapture = lastCaptured && 
                lastCaptured.type === type && 
                lastCaptured.captured_by_color === (viewerColor === 'white' ? 'black' : 'white');
              
              return (
                <div
                  key={type}
                  className="flex items-center gap-1"
                  role="listitem"
                >
                  <AnimatePresence mode="wait">
                    {isNewCapture ? (
                      <motion.div
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 1.15, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 18, mass: 0.7 }}
                      >
                        <Piece type={type} color={viewerColor || 'white'} size={pieceSize} />
                      </motion.div>
                    ) : (
                      <Piece type={type} color={viewerColor || 'white'} size={pieceSize} />
                    )}
                  </AnimatePresence>
                  {count > 1 && (
                    <span
                      className={cn(
                        'text-xs font-mono font-semibold text-destructive',
                        compact && 'text-[10px]',
                      )}
                    >
                      ×{count}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}, (prev, next) => {
  // Only re-render if graveyard length or viewerColor changes
  return (
    prev.graveyard.length === next.graveyard.length &&
    prev.viewerColor === next.viewerColor &&
    prev.compact === next.compact &&
    prev.captureTrigger === next.captureTrigger
  );
});

Graveyard.displayName = 'Graveyard';